import $ from 'jquery';
import { track } from 'util/analytics';
import * as csrf from 'util/csrf';
import { showGenericModal } from 'util/ui';
import { isNative } from 'util/global';
import { getFormData } from 'util/getFormData';
import { showTimeoutErrorModal, errorModalHandler } from 'app/error_modal';
import { BEvents } from '../app/events';

/**
 * Private method for sending requests for the Ajax class. This was formerly a method on the Ajax
 * class but was moved because a) it's only used internally by the Ajax class and b) there may be
 * some corruption happening between this send method and other send methods in the Segment module
 * (but still not sure how or why).
 * @param {Ajax} ajaxInstance
 * @param {String} url
 * @param {Object} data
 * @param {String} type
 */
function sendRequest(ajaxInstance, url = '', data = {}, type = 'POST') {
    data.page_key = data.page_key || ajaxInstance.key;
    data.version = data.version || window.VERSIONS[data.page_key] || 1;
    data.deferred = data.deferred || false;
    data.timeout = data.timeout || 90000;

    if (!isNative && !csrf.isSameOrigin(url)) {
        return ajaxInstance.error({
            error: 'csrf',
            responseText: 'CSRF error',
            status: 0,
        });
    }

    $.ajax({
        url,
        data,
        type,
        timeout: data.timeout,
        cache: false,

        success(result) {
            // if there's a redirect, do that before bothering with this other stuff
            if (typeof result.redirect === 'string') {
                // check for a specificized redirect handler
                if (data.redirectHandler) {
                    // `webpack mode: weak` means that we won't request this module, we'll just
                    // hope that it was already included somewhere else. This is necessary for
                    // the native entrypoint where we don't need this module.
                    import(/* webpackMode: 'weak' */ 'app/generic_controllers')
                        .then(({ executeFunctionByName }) => executeFunctionByName(
                            data.redirectHandler,
                            result.redirect,
                            data
                        ));
                    // otherwise, do the redirect
                } else {
                    window.location = result.redirect;
                }
            }

            // loop through all actions
            $.each(result.actions || {}, (actionKey, action) => {
                // store the current version of the action, or -1 if it doesn't have a version
                const currentVersion = (window.VERSIONS[action.component_key]
                    || window.VERSIONS[actionKey] || 1);

                // If the current version matches the returned version, nothing's changed.
                // Success!
                if (currentVersion === action.version) {
                    // action.result no longer exists in the response
                    if (action.result && action.result.reload) {
                        ajaxInstance.get(action.result.reload, {
                            param_key: actionKey,
                            version: currentVersion,
                        });
                    } else {
                        ajaxInstance.success(actionKey, action, data.deferred);
                    }
                } else {
                    // update the version
                    window.VERSIONS[action.component_key] = action.version;

                    // save the failure mode
                    let failureMode = action.js_failure_mode;

                    // if versions are more than one version apart
                    if (Math.abs(currentVersion - action.version) > 1) {
                        failureMode = 'hard';
                    }

                    $('.hide-on-error').hide();

                    switch (failureMode) {
                        case 'soft_with_update':
                            // display a warning, pass the new data to existing handler function
                            showGenericModal('Warning', `Some of the information in that request
                                was out of date due to changes to our server code. We're updating
                                the page.`);
                            ajaxInstance.success(actionKey, action);
                            break;
                        case 'soft_without_update':
                            // display a warning, no update
                            showGenericModal('Warning', `Some of the information in that request
                                was out of date due to changes to our server code. You must refresh
                                the page and try again.`);
                            break;
                        case 'no_warn_no_update':
                            // do nothing
                            break;
                        case 'hard':
                            // force refresh
                            window.location.reload();
                            break;
                        default:
                            throw new Error(`Unknown failure mode ${failureMode}`);
                    }
                }
            });

            $.each(result.ajax_tracked_events || {}, (i, trackedEvent) => {
                track(trackedEvent.event_name, trackedEvent.event_properties);
            });

            $.each(result.messages || {}, (i, message) => {
                if (message.message_type === 'error') {
                    showGenericModal('Error', message.message_text);
                } else {
                    showGenericModal('Warning', message.message_text);
                }
            });
        },

        error(response) {
            ajaxInstance.error(response, data);
        },
    });

    return this;
}

export class Ajax {
    /** @param {string} key */
    constructor(key) {
        this.key = key || false;
    }

    /**
     * @param {string} url
     * @param {object} data
     * @returns {Ajax}
     */
    post(url, data) {
        return sendRequest(this, url, data, 'POST');
    }

    /**
     * @param {string} url
     * @param {object} data
     * @returns {Ajax}
     */
    get(url, data) {
        return sendRequest(this, url, data, 'GET');
    }

    form(selector = '', data = {}) {
        const formData = {};
        const $form = $(selector);

        // if there's no form returned from the query...
        if ($form.length === 0) {
            // there's no form to send
            return this;
        }

        // if there's a url in the data obj, use that. otherwise, use the form's action
        let url = typeof data.url === 'string' ? data.url : $form.attr('action');
        url = url || '';
        url = url.length === 0 ? window.location.href : url;

        // combine the form data with override data
        const combinedData = Object.assign({}, formData, getFormData($form), data, $form.data());

        // disable the buttons
        $form.find('button, input[type=submit], .btn')
            .filter((i, button) => $(button).parents('.viz-wrapper').length === 0)
            .prop('disabled', 'disabled')
            .addClass('dim');

        // send it
        return sendRequest(this, url, combinedData, 'POST');
    }

    error(response, data = {}) {
        $('.hide-on-error').hide();
        response.status = response.status || 0;

        // process data
        data.page_key = this.key;
        delete data.version;
        delete data.csrfmiddlewaretoken;
        delete data.deferred;

        if (response.statusText === 'timeout') {
            showTimeoutErrorModal();

            if (data.timeoutHandler) {
                import(/* webpackMode: 'weak' */ 'app/generic_controllers')
                    .then(({ executeFunctionByName }) => {
                        executeFunctionByName(data.timeoutHandler, data);
                        delete data.timeoutHandler;
                    });
            }

            track('timeout_errors', data);
        } else if (response.status > 0) {
            errorModalHandler(response);

            // track the error
            data.status = response.status;
            track('ajax_errors', data);
        }
    }

    // eslint-disable-next-line class-methods-use-this
    success(actionKey, action, deferred) {
        const callback = window.vkey[action.component_key] || window.vkey.handle;
        callback(action, actionKey, deferred || false);
        if (actionKey in this.actionKeysToNotifyCompletion) {
            window.dispatchEvent(BEvents[this.actionKeysToNotifyCompletion[actionKey]].instance);
        }
    }

    // To dispatch an event when your component is loaded add the actionKey as the key in the
    // object below and the event's key as the value. You can find or add a new in app/events.js
    actionKeysToNotifyCompletion = {
        notifications_page: 'notificationsLoaded',
    };
}


/** @param {string} key */
export default function ajax(key) {
    return new Ajax(key);
}
